import React from 'react';

/**
 * Atom — Nookli's AI agent. The brand mark for Atom across the app:
 * profile avatar, AI badge, friend list, hero on solo setup. No face,
 * no humanlike features — Atom is a *presence*, not a character. Three
 * orbiting electrons around a coral nucleus, gently rotating to feel
 * alive without being noisy.
 *
 * Why a custom mark and not a lucide Bot icon: Atom is brand, not
 * iconography. It needs to read at any size and feel different from
 * generic AI chat bubbles.
 */

export interface AtomMarkProps {
  size?: number;
  /** Animate the orbits (default true). Disable on lists / chip rows. */
  animated?: boolean;
  /** When set, render against a solid coral disc background — used as
   *  a profile-avatar replacement. */
  filled?: boolean;
  /** Override the nucleus + ring colors — for dark theme or special states. */
  color?: string;
}

export function AtomMark({ size = 40, animated = true, filled = false, color }: AtomMarkProps) {
  const accent = color || 'var(--accent, #FF5A1F)';
  const accent2 = color || 'var(--accent-2, #FF7A47)';
  // Backdrop: when filled, a coral disc; otherwise a subtle warm ring.
  const bg = filled ? accent : 'var(--accent-soft, #FFE8DD)';
  const stroke = filled ? '#fff' : accent;
  const electron = filled ? '#fff' : accent2;
  const id = React.useId();

  return (
    <div
      className={animated ? 'wr-atom-anim' : ''}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: filled ? `0 0 0 2px var(--surface, #fff), 0 6px 20px -8px ${accent}` : 'none',
      }}
      aria-label="Atom"
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        style={{ position: 'absolute', inset: 0 }}
      >
        <defs>
          <radialGradient id={`${id}-glow`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={electron} stopOpacity="0.35" />
            <stop offset="100%" stopColor={electron} stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Soft glow behind the orbits */}
        <circle cx="32" cy="32" r="22" fill={`url(#${id}-glow)`} />

        {/* Three orbits — three rings rotated 60° apart so they look
            like an atom from any angle. Stroke uses currentColor for
            theme overrides via the parent. */}
        <g style={{ transformOrigin: '32px 32px' }} className={animated ? 'wr-atom-orbit-a' : ''}>
          <ellipse cx="32" cy="32" rx="22" ry="10" stroke={stroke} strokeWidth="1.6" strokeOpacity="0.55" />
        </g>
        <g style={{ transformOrigin: '32px 32px', transform: 'rotate(60deg)' }} className={animated ? 'wr-atom-orbit-b' : ''}>
          <ellipse cx="32" cy="32" rx="22" ry="10" stroke={stroke} strokeWidth="1.6" strokeOpacity="0.55" />
        </g>
        <g style={{ transformOrigin: '32px 32px', transform: 'rotate(-60deg)' }} className={animated ? 'wr-atom-orbit-c' : ''}>
          <ellipse cx="32" cy="32" rx="22" ry="10" stroke={stroke} strokeWidth="1.6" strokeOpacity="0.55" />
        </g>

        {/* Electrons — small dots traveling on each orbit. Use the same
            transform-origin so they ride the orbit ring exactly. */}
        <g style={{ transformOrigin: '32px 32px' }} className={animated ? 'wr-atom-orbit-a' : ''}>
          <circle cx="54" cy="32" r="2.4" fill={electron} />
        </g>
        <g style={{ transformOrigin: '32px 32px', transform: 'rotate(60deg)' }} className={animated ? 'wr-atom-orbit-b' : ''}>
          <circle cx="54" cy="32" r="2.4" fill={electron} />
        </g>
        <g style={{ transformOrigin: '32px 32px', transform: 'rotate(-60deg)' }} className={animated ? 'wr-atom-orbit-c' : ''}>
          <circle cx="54" cy="32" r="2.4" fill={electron} />
        </g>

        {/* Nucleus — solid coral dot, slightly oversized so it reads
            well even at very small sizes (16px nav badges). */}
        <circle cx="32" cy="32" r="6.5" fill={stroke} />
        <circle cx="29.5" cy="29.5" r="2" fill="rgba(255,255,255,0.45)" />
      </svg>
    </div>
  );
}

/* Inject the orbit animation keyframes once. Three orbits at slightly
   different speeds so the motion feels organic, not mechanical. */
const styleId = 'wr-atom-anim';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const s = document.createElement('style');
  s.id = styleId;
  s.textContent = `
    @keyframes wr-atom-spin-a { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
    @keyframes wr-atom-spin-b { 0% { transform: rotate(60deg); } 100% { transform: rotate(420deg); } }
    @keyframes wr-atom-spin-c { 0% { transform: rotate(-60deg); } 100% { transform: rotate(300deg); } }
    .wr-atom-anim .wr-atom-orbit-a { animation: wr-atom-spin-a 6s linear infinite; }
    .wr-atom-anim .wr-atom-orbit-b { animation: wr-atom-spin-b 8s linear infinite; }
    .wr-atom-anim .wr-atom-orbit-c { animation: wr-atom-spin-c 10s linear infinite; }
    @media (prefers-reduced-motion: reduce) {
      .wr-atom-anim .wr-atom-orbit-a,
      .wr-atom-anim .wr-atom-orbit-b,
      .wr-atom-anim .wr-atom-orbit-c { animation: none; }
    }
  `;
  document.head.appendChild(s);
}
